import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { authApi } from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const persistToken = (token) => {
    if (token) localStorage.setItem('swiftdash_token', token);
    else localStorage.removeItem('swiftdash_token');
  };

  const checkAuth = useCallback(async () => {
    try {
      const me = await authApi.me();
      setUser(me);
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Don't run /me if we're returning from Emergent OAuth (session_id hash)
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async ({ email, password }) => {
    const { access_token, user: u } = await authApi.login({ email, password });
    persistToken(access_token);
    setUser(u);
    return u;
  };

  const signup = async ({ name, email, password }) => {
    const { access_token, user: u } = await authApi.signup({ name, email, password });
    persistToken(access_token);
    setUser(u);
    return u;
  };

  // Start Emergent OAuth (redirect)
  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Called by AuthCallback after exchanging session_id
  const completeGoogleAuth = async (sessionId) => {
    const { access_token, user: u } = await authApi.google(sessionId);
    persistToken(access_token);
    setUser(u);
    return u;
  };

  const logout = async () => {
    try { await authApi.logout(); } catch (e) { /* ignore */ }
    persistToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, loginWithGoogle, completeGoogleAuth, logout, refresh: checkAuth, refreshUser: setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
